import React from "react";
import * as Sentry from "@sentry/react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  Typography,
  Divider,
  Stack,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BugReportIcon from "@mui/icons-material/BugReport";
import { getQuotaUsage, resetQuotaCounts } from "../../utils/sentry-quota";

/**
 * Sentry Debug Page for testing error tracking and monitoring
 * Only visible in development environment
 */
export const SentryDebugPage: React.FC = () => {
  const theme = useTheme();
  const [quotaUsage, setQuotaUsage] = React.useState(getQuotaUsage());

  const handleRefreshQuota = () => {
    setQuotaUsage(getQuotaUsage());
  };

  const handleResetQuota = () => {
    resetQuotaCounts();
    setQuotaUsage(getQuotaUsage());
  };

  const testCriticalError = () => {
    try {
      throw new Error("Test critical error from Sentry Debug page");
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          priority: "critical",
          test: "true",
        },
      });
    }
  };

  const testNormalError = () => {
    try {
      throw new Error("Test normal error from Sentry Debug page");
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          priority: "normal",
          test: "true",
        },
      });
    }
  };

  const testBreadcrumb = () => {
    Sentry.addBreadcrumb({
      category: "test",
      level: "info",
      message: "Test breadcrumb from Sentry Debug page",
      data: {
        testData: "This is test breadcrumb data",
        timestamp: new Date().toISOString(),
      },
    });
    alert("Breadcrumb added. Check Sentry dashboard.");
  };

  const startTransaction = () => {
    // Use the newer Sentry API for performance monitoring
    const startTime = performance.now();
    
    // Simulate work
    setTimeout(() => {
      const duration = performance.now() - startTime;
      Sentry.captureMessage(`Test transaction completed in ${duration.toFixed(0)}ms`, "info");
      alert("Transaction completed. Check Sentry dashboard.");
    }, 2000);
  };

  const handleUnhandledError = () => {
    // This will trigger the error boundary
    throw new Error("Unhandled error from Sentry Debug page - this should trigger error boundary");
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 1 }}>
        <BugReportIcon sx={{ fontSize: 28, color: theme.palette.info.main }} />
        <Typography variant="h4" component="h1">
          Sentry Debug Testing
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ mb: 3, color: theme.palette.text.secondary }}>
        This page is for testing Sentry error tracking and monitoring. Use the buttons below to trigger
        various error scenarios and verify they are captured correctly in the Sentry dashboard.
      </Typography>

      {/* Error Testing Section */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Error Capture Testing" />
        <Divider />
        <CardContent>
          <Stack direction="column" spacing={2}>
            <Button
              variant="contained"
              color="error"
              onClick={testCriticalError}
              fullWidth
            >
              Throw Critical Error
            </Button>

            <Button
              variant="contained"
              color="warning"
              onClick={testNormalError}
              fullWidth
            >
              Throw Normal Error
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={handleUnhandledError}
              fullWidth
            >
              Trigger Error Boundary
            </Button>

            <Alert severity="info">
              Critical errors bypass quota limits. Normal errors are subject to quota management.
            </Alert>
          </Stack>
        </CardContent>
      </Card>

      {/* Breadcrumb & Transaction Testing */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Breadcrumbs & Transactions" />
        <Divider />
        <CardContent>
          <Stack direction="column" spacing={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={testBreadcrumb}
              fullWidth
            >
              Add Test Breadcrumb
            </Button>

            <Button
              variant="outlined"
              color="primary"
              onClick={startTransaction}
              fullWidth
            >
              Start Test Transaction (2s)
            </Button>

            <Alert severity="info">
              Breadcrumbs help track user actions leading to errors. Transactions measure
              performance and execution time.
            </Alert>
          </Stack>
        </CardContent>
      </Card>

      {/* Quota Usage Section */}
      <Card>
        <CardHeader
          title="Quota Usage (Current Month)"
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="outlined" onClick={handleRefreshQuota}>
                Refresh
              </Button>
              <Button size="small" variant="outlined" color="warning" onClick={handleResetQuota}>
                Reset (Dev Only)
              </Button>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Month:</strong> {quotaUsage.month}
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                  <TableCell>
                    <strong>Quota Type</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Used</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Limit</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Usage %</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Errors</TableCell>
                  <TableCell align="right">{quotaUsage.errors.used}</TableCell>
                  <TableCell align="right">{quotaUsage.errors.limit}</TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 8,
                          bgcolor: theme.palette.action.disabled,
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${Math.min(quotaUsage.errors.percentage, 100)}%`,
                            bgcolor:
                              quotaUsage.errors.percentage > 90
                                ? theme.palette.error.main
                                : quotaUsage.errors.percentage > 70
                                ? theme.palette.warning.main
                                : theme.palette.success.main,
                            transition: "all 0.3s ease",
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ minWidth: 40 }}>
                        {quotaUsage.errors.percentage.toFixed(1)}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>Transactions</TableCell>
                  <TableCell align="right">{quotaUsage.transactions.used}</TableCell>
                  <TableCell align="right">{quotaUsage.transactions.limit}</TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 8,
                          bgcolor: theme.palette.action.disabled,
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${Math.min(quotaUsage.transactions.percentage, 100)}%`,
                            bgcolor:
                              quotaUsage.transactions.percentage > 90
                                ? theme.palette.error.main
                                : quotaUsage.transactions.percentage > 70
                                ? theme.palette.warning.main
                                : theme.palette.success.main,
                            transition: "all 0.3s ease",
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ minWidth: 40 }}>
                        {quotaUsage.transactions.percentage.toFixed(1)}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>Replays</TableCell>
                  <TableCell align="right">{quotaUsage.replays.used}</TableCell>
                  <TableCell align="right">{quotaUsage.replays.limit}</TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 8,
                          bgcolor: theme.palette.action.disabled,
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${Math.min(quotaUsage.replays.percentage, 100)}%`,
                            bgcolor:
                              quotaUsage.replays.percentage > 90
                                ? theme.palette.error.main
                                : quotaUsage.replays.percentage > 70
                                ? theme.palette.warning.main
                                : theme.palette.success.main,
                            transition: "all 0.3s ease",
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ minWidth: 40 }}>
                        {quotaUsage.replays.percentage.toFixed(1)}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Alert severity="warning">
            Sentry free tier limits: 5,000 errors, 10,000 transactions, 50 replays per month.
            Critical errors bypass limits. Sample rates auto-adjust at 90% threshold.
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SentryDebugPage;
